import React, { useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { getAuthErrorMessage } from "../utils/authErrors";

/* Icons */
const Eye = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      d="M1.5 12s3.75-7.5 10.5-7.5S22.5 12 22.5 12 18.75 19.5 12 19.5 1.5 12 1.5 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const EyeOff = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M1.5 12s3.75-7.5 10.5-7.5c2.1 0 3.98.62 5.6 1.62M22.5 12s-3.75 7.5-10.5 7.5c-2.1 0-3.98-.62-5.6-1.62"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M9.4 9.4a3.5 3.5 0 004.9 4.9"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset password modal state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetMsg, setResetMsg] = useState("");

  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  async function enforceAdminAccess(user) {
    // 1) Check users/{uid}.role === "admin"
    const userSnap = await getDoc(doc(db, "users", user.uid));
    if (!userSnap.exists()) {
      throw new Error(
        "Admin profile missing. Create users/{uid} with role='admin'.",
      );
    }
    const u = userSnap.data() || {};
    if (String(u.role || "").toLowerCase() !== "admin") {
      throw new Error("Access denied. This portal is for admins only.");
    }

    // 2) Optional extra gate: admins/{uid} exists + active
    // If you don't want this extra gate, you can delete this block.
    const adminSnap = await getDoc(doc(db, "admins", user.uid));
    if (adminSnap.exists()) {
      const a = adminSnap.data() || {};
      if (String(a.status || "active").toLowerCase() !== "active") {
        throw new Error("Admin account is not active.");
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // ✅ 1) Firebase Auth sign-in
      const userCred = await signInWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password,
      );

      // ✅ 2) Firestore admin checks
      await enforceAdminAccess(userCred.user);

      // ✅ 3) Success
      navigate("/dashboard");
    } catch (err) {
      console.error("ADMIN LOGIN ERROR:", err);

      // If auth succeeded but admin check failed, sign out so session isn't kept
      try {
        if (auth.currentUser) await signOut(auth);
      } catch {}

      setError(getAuthErrorMessage(err, "login"));
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetError("");
    setResetMsg("");
    setResetEmail(form.email || "");
    setResetOpen(true);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetMsg("");
    const email = (resetEmail || "").trim();
    if (!email) {
      setResetError("Please enter your email.");
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg("If an account exists, a reset link has been sent.");
    } catch (err) {
      setResetError(getAuthErrorMessage(err, "resetPassword"));
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (!resetOpen) return;
    const onKey = (e) => e.key === "Escape" && setResetOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resetOpen]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <form
        className="w-full max-w-md bg-white shadow-2xl rounded-2xl p-8"
        onSubmit={handleSubmit}
      >
        <h2 className="text-3xl font-bold mb-1 text-center text-blue-600">
          Admin Login
        </h2>
        <p className="text-center text-gray-500 mb-6 text-sm">
          Enter your credentials to access the dashboard
        </p>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
            {error}
          </div>
        )}

        <div className="mb-4">
          <input
            id="email"
            type="email"
            name="email"
            placeholder="Email"
            className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            required
          />
        </div>

        <div className="mb-2">
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              className="w-full px-4 py-2 pr-11 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200"
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-2 my-auto p-2 rounded-md text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        <div className="mb-6 flex justify-end">
          <button
            type="button"
            onClick={openReset}
            className="text-sm text-blue-700 hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 rounded-xl text-white font-semibold transition ${
            loading
              ? "bg-blue-300 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>

      {resetOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setResetOpen(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 relative">
              <button
                className="absolute top-3 right-4 text-2xl leading-none"
                aria-label="Close reset password dialog"
                onClick={() => setResetOpen(false)}
              >
                ×
              </button>

              <h3 className="text-xl font-bold mb-1">Reset password</h3>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email and we’ll send you a link to reset your
                password.
              </p>

              {resetError && (
                <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
                  {resetError}
                </div>
              )}
              {resetMsg && (
                <div className="mb-3 rounded-lg bg-green-50 text-green-700 px-3 py-2 text-sm">
                  {resetMsg}
                </div>
              )}

              <form onSubmit={handleReset}>
                <label htmlFor="reset-email" className="text-sm font-medium">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  className="mt-1 mb-4 w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  required
                />

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl border"
                    onClick={() => setResetOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className={`px-4 py-2 rounded-xl text-white font-semibold transition ${
                      resetLoading
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {resetLoading ? "Sending..." : "Send reset link"}
                  </button>
                </div>
              </form>

              <p className="mt-3 text-xs text-gray-500">
                Tip: check your spam folder if you don’t see the email.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
